import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { Download } from 'lucide-react';
import { useLanguage } from '../../lib/LanguageContext';

export default function Transactions() {
  const { t } = useLanguage();
  const [items, setItems] = useState([]);
  const [type, setType] = useState('all');
  const [search, setSearch] = useState('');
  const [period, setPeriod] = useState('30');

  const load = async () => {
    const params = {};
    if (type !== 'all') params.type = type;
    if (period !== 'all') {
      const d = new Date(); d.setDate(d.getDate() - parseInt(period, 10));
      params.since = d.toISOString();
    }
    if (search) params.search = search;
    const r = await api.get('/client/transactions', { params });
    setItems(r.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [type, period]);

  const totalIn = useMemo(() => items.filter((i) => i.montant > 0).reduce((a, b) => a + b.montant, 0), [items]);
  const totalOut = useMemo(() => items.filter((i) => i.montant < 0).reduce((a, b) => a + b.montant, 0), [items]);

  const exportCsv = () => {
    const header = 'Date,Libellé,Type,Montant,Solde après\n';
    const rows = items.map((tx) => `${tx.date_transaction},"${(tx.libelle || '').replace(/"/g, '\\"')}",${tx.type},${tx.montant.toFixed(2)},${(tx.solde_apres || 0).toFixed(2)}`).join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `transactions_BFI_${new Date().toISOString().slice(0,10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">{t('transactions.title')}</h1>
        <Button variant="outline" onClick={exportCsv} data-testid="btn-export-csv"><Download className="w-4 h-4 mr-1"/> {t('transactions.exportCsv')}</Button>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div className="stat-card"><div className="text-slate-500 text-xs uppercase">{t('transactions.credits')}</div><div className="text-2xl font-bold mt-1 text-emerald-600">+{totalIn.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</div></div>
        <div className="stat-card"><div className="text-slate-500 text-xs uppercase">{t('transactions.debits')}</div><div className="text-2xl font-bold mt-1 text-rose-600">{totalOut.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</div></div>
        <div className="stat-card"><div className="text-slate-500 text-xs uppercase">{t('transactions.net')}</div><div className="text-2xl font-bold mt-1">{(totalIn + totalOut).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</div></div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200">
        <div className="p-4 border-b grid md:grid-cols-4 gap-3">
          <Input placeholder={t('transactions.search')} value={search} onChange={(e) => setSearch(e.target.value)} onBlur={load} data-testid="input-tx-search"/>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger data-testid="select-tx-type"><SelectValue placeholder={t('common.type')}/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('transactions.allTypes')}</SelectItem>
              <SelectItem value="virement_recu">{t('transactions.transferReceived')}</SelectItem>
              <SelectItem value="virement_emis">{t('transactions.transferSent')}</SelectItem>
              <SelectItem value="paiement_carte">{t('transactions.cardPayment')}</SelectItem>
              <SelectItem value="frais">{t('transactions.fees')}</SelectItem>
              <SelectItem value="remboursement">{t('transactions.refund')}</SelectItem>
            </SelectContent>
          </Select>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger data-testid="select-tx-period"><SelectValue placeholder={t('transactions.period')}/></SelectTrigger>
            <SelectContent>
              <SelectItem value="7">{t('transactions.last7Days')}</SelectItem>
              <SelectItem value="30">{t('transactions.last30Days')}</SelectItem>
              <SelectItem value="90">{t('transactions.last3Months')}</SelectItem>
              <SelectItem value="180">{t('transactions.last6Months')}</SelectItem>
              <SelectItem value="365">{t('transactions.lastYear')}</SelectItem>
              <SelectItem value="all">{t('common.all')}</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={load} className="bg-blue-700" data-testid="btn-tx-apply">{t('common.filter')}</Button>
        </div>
        <div className="divide-y">
          {items.length === 0 && <div className="p-8 text-center text-slate-500 text-sm">{t('transactions.noTransactions')}</div>}
          {items.map((tx) => (
            <div key={tx.id} className="p-4 flex items-center justify-between">
              <div>
                <div className="font-medium">{tx.libelle}</div>
                <div className="text-xs text-slate-500">{new Date(tx.date_transaction).toLocaleDateString('fr-FR')} • {tx.type}</div>
              </div>
              <div className="text-right">
                <div className={`font-bold ${tx.montant >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                  {tx.montant >= 0 ? '+' : ''}{tx.montant.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                </div>
                <div className="text-xs text-slate-500">{t('transactions.balance')} : {(tx.solde_apres || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
